import { collection } from '@/infra/collection'
import type { JobsApi } from '@/features/jobs/api/job-api'
import {
  JobProgression,
  type ProgressionStepStatus,
} from '@/features/jobs/taxes/progression/JobProgression'
import DataLoader from 'dataloader'
import {
  GetManyJobProgressionsRequest,
  JobProgression as JobProgressionProto,
  JobProgressionFlow as FlowProto,
  type JobProgressionRealtimeEvent as JobProgressionRealtimeEventProto,
  type UpdateJobProgressionResponse as UpdateJobProgressionResponseProto,
  SetDraftRejectedProgressionRequest,
  SetDraftApprovedProgressionRequest,
  SetReadyToFileProgressionRequest,
} from '@taxfyle/api-internal/internal/job_progression_pb'
import { runInAction } from 'mobx'
import {
  mapProgressionFoundToProps,
  mapProgressionRealtimeEventToProps,
} from '@/features/jobs/taxes/progression/progression-mappers'
import { Id } from '@/infra/id'
import { tap } from 'rxjs/operators'
import { map } from 'rxjs'
import { filterNonNil } from '@/utils/rx-util'
import type { PrimeJobProgressionProps } from '@/features/jobs'

/**
 * Manages job progressions.
 */
export class ProgressionStore {
  private readonly _loader = new DataLoader(this._fetchManyCore.bind(this))

  /**
   * Tracked progressions by job ID.
   */
  readonly progressions = collection({
    create: () => new JobProgression(),
    getDataId: (d) => d.jobId,
    getModelId: (m) => m.jobId,
  })

  constructor(private readonly _api: JobsApi) {
    this._api.realtime$
      .pipe(
        map((event) => event.getJobProgression()),
        filterNonNil(),
        tap(this._onRemoteEvent.bind(this)),
      )
      .subscribe()
  }

  /**
   * Primes the {@link JobProgression} with the given props and fetches it in the background.
   */
  prime(props: PrimeJobProgressionProps) {
    void this.fetchProgression(props.id)

    return this.progressions.set({
      jobId: props.id,
    })
  }

  /**
   * Fetches a job progression.
   *
   * @param id
   */
  fetchProgression(id: Id): Promise<JobProgression | null> {
    return this._loader.load(Id.assert(id))
  }

  /**
   * Transitions the progression to the specified step.
   *
   * @param id
   * @param nextStep
   */
  async transitionToStep(id: Id, nextStep: ProgressionStepStatus) {
    id = Id.assert(id)
    let response: UpdateJobProgressionResponseProto | null = null
    switch (nextStep) {
      case 'DraftRejected':
        response = await this._api.progressions.setDraftRejectedProgression(
          new SetDraftRejectedProgressionRequest().setJobId(id),
        )
        break
      case 'DraftApproved':
        response = await this._api.progressions.setDraftApprovedProgression(
          new SetDraftApprovedProgressionRequest().setJobId(id),
        )
        break
      case 'ReadyToFile':
        response = await this._api.progressions.setReadyToFileProgression(
          new SetReadyToFileProgressionRequest().setJobId(id),
        )
        break
      default:
        console.error('Unhandled next step', nextStep, 'for job', id)
        break
    }

    if (!response) {
      return
    }

    const progression = this.progressions.get(id)
    if (!progression) {
      // Shouldn't really happen.
      return
    }

    // Re-fetch the progression.
    this._loader.clear(id)
    await this.fetchProgression(id)
  }

  /**
   * Fetches the job progressions.
   *
   * @param ids
   * @private
   */
  private async _fetchManyCore(
    ids: readonly string[],
  ): Promise<(JobProgression | null)[]> {
    const request = new GetManyJobProgressionsRequest().setJobIdsList(
      ids.slice(),
    )

    const response =
      await this._api.progressions.getManyJobProgressions(request)

    const result: (JobProgression | null)[] = []
    runInAction(() => {
      const protos = response.getProgressionsList() ?? []
      for (const id of ids) {
        const proto = protos.find(
          (p) =>
            p.getTypeCase() === JobProgressionProto.TypeCase.PROGRESSION &&
            p.getProgression()?.getJobId() === id,
        )

        if (!proto) {
          // Issue retrieving; return null.
          result.push(null)
          continue
        }

        const progressionProto = proto.getProgression()
        if (progressionProto?.getProgressionFlow() === FlowProto.NONE) {
          // No flow; return null.
          result.push(null)
          continue
        }

        const props = mapProgressionFoundToProps(progressionProto)
        const progression = this.progressions.set(props)
        result.push(progression)
      }
    })

    return result
  }

  /**
   * Handles remote events from the server.
   *
   * @param event
   * @private
   */
  private _onRemoteEvent(event: JobProgressionRealtimeEventProto) {
    const props = mapProgressionRealtimeEventToProps(event)
    this.progressions.set(props)
  }
}
